import React, { type ReactElement } from 'react';

import classnames from 'classnames';

import { Step } from './components/Step/Step';

import styles from './Stepper.module.scss';

export type StepperItem = {
    title?: string;
    icon?: React.ReactNode;
};

export type StepperStatus = 'process' | 'finish' | 'wait';

export type StepperType = 'number' | 'dot' | 'line';

export type StepperProps = {
    /**
     * Additional class to Steps.
     */
    className?: string;
    /**
     * The steps to display.
     */
    items: StepperItem[];
    /**
     * The type of the stepper.
     * - `numbers`: the default stepper containing numbers for each step.
     * - `dot`: a smaller version where we replace the steps numbers by dots (use when the there is not enough vertical space).
     * - `line`: a line version where we replace the steps numbers by a line (use when the there is not enough vertical space).
     */
    type?: StepperType;
    /**
     * To set the current step, counting from 0.
     */
    current?: number;
    /**
     * Trigger when Step is changed.
     */
    onChange?: (current: number) => void;
    style?: React.CSSProperties;
};

export const Stepper = ({
    className,
    current = 0,
    onChange,
    items = [],
    type = 'number',
    ...rest
}: StepperProps): ReactElement => {
    const onStepClick = (next: number) => {
        if (onChange && current !== next) {
            onChange(next);
        }
    };

    const renderStep = (item: StepperItem, index: number) => {
        const stepNumber = index + 1;
        return (
            <Step
                {...item}
                status={index < current ? 'finish' : 'wait'}
                active={index === current}
                stepNumber={stepNumber}
                stepIndex={index}
                last={index === items.length - 1}
                key={`step-${index}`}
                type={type}
                onStepClick={onChange && onStepClick}
            />
        );
    };

    return (
        <div className={classnames(styles.stepper, styles[type], className)} {...rest}>
            {items.filter(Boolean).map<React.ReactNode>(renderStep)}
        </div>
    );
};
